/* eslint-disable camelcase */
import { TrackingLoginStatus } from '@vfde-sails/tracking';
import { TrackingPayload } from './interface';
import { TrackingFootprint, TrackingPageType, TrackingProductVendor, TrackingStepName } from './enum';
import { CableProvider } from '@vfde-sails/avc-core';
import { JarvisSuggestion } from '../interface';
import { AccessSupplierType, AvailabilityIndicator, FiberConnectedIndicator, HtmlId, NewAvailabilityIndicator } from '../enum';
import { NOTIFICATION_HEADLINE_CLASSNAME } from '@vfde-brix/ws10/notification';
import { TRACKING_FIBER_AVAILABILITY } from './constants';

/**
 * Save vodafone siteStructure into a constant to preserve the original array on page load,
 * because DIP script overwrites VF.siteStructure every time we call tracking
 */
export const pageLevels = window.VF?.siteStructure
    ? [...window.VF.siteStructure]
    : [];

export const getCableType = (value: CableProvider) => TrackingProductVendor[value];

export const getAvailableBandwidth = (primaryDownload = 0, secondaryDownload = 0) => {
    const value = Math.max(primaryDownload, secondaryDownload);

    return `${value} mbit`;
};

export const getProductFootPrint = (isUnity: boolean | undefined) =>
    isUnity ? TrackingFootprint.UME : TrackingFootprint.KDG;

export const getPageLevels = (step: TrackingStepName) => pageLevels.concat([
    'availability check',
    step,
]);

/**
 * Get hotline numbers for tracking
 * @returns {string[]}
 */
export const getHotlineNumbers = () => window.VF?.hotlineNumbers || [];

export const getGeneralPageView = (pageType: TrackingPageType): TrackingPayload => ({
    login_status: TrackingLoginStatus.NotLoggedIn,
    page_levels: pageLevels,
    page_type: pageType,
    site_area: 'fixnet',
    platform_type: 'responsive',
});

export const getRecommendedProducts = (primary?: JarvisSuggestion, secondary?: JarvisSuggestion): string[] => {
    const result: string[] = [];

    if (primary?.product?.id) {
        result.push(primary.product.id);
    }

    if (secondary?.product?.id) {
        result.push(secondary.product.id);
    }

    return result;
};

export const getAvailableProductTypes = (primary?: JarvisSuggestion, secondary?: JarvisSuggestion): string[] => {
    const result: string[] = [];

    if (primary?.type) {
        result.push(primary.type.toLowerCase());
    }

    if (secondary?.type) {
        result.push(secondary.type.toLowerCase());
    }

    return result;
};

/**
 * Formats the given string to lowercase, separates words
 * if they are next to each other without space and removes
 * any additional whitespaces and replaces the special
 * German letters in the text to basic latin letters
 * (e.g. `ß` to `ss`, `ä` to `ae`, and so on).
 * This step is requested by Digital Power,
 * to process the incoming data more easily.
 */
export const formatToTrackingString = (text: string, maxLength = 100): string =>
    text.replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .substring(0, maxLength)
        .replace(/\u00e4/g, 'ae')
        .replace(/\u00f6/g, 'oe')
        .replace(/\u00fc/g, 'ue')
        .replace(/\u00df/g, 'ss');

/**
 * Gets the innerHTML text for Notification Headline components within an Id specified element.
 * @param {HtmlId} id - element Id
 * @returns {string}
 */
export const getNotificationHeadlineText = (id: HtmlId): string =>
    document.getElementById(id)?.getElementsByClassName(NOTIFICATION_HEADLINE_CLASSNAME)[0]?.innerHTML ?? '';

export const getAvailabilityFiberProviders = (primary?: JarvisSuggestion, secondary?: JarvisSuggestion): string[] => {
    const result: string[] = [];

    const availabilityMap: Record<string, Record<string, TRACKING_FIBER_AVAILABILITY>> = {
        [AccessSupplierType.VFDE]: {
            [AvailabilityIndicator.PRE_SALES]: TRACKING_FIBER_AVAILABILITY.VODAFONE_PRESALES,
            [FiberConnectedIndicator.CONNECTED]: TRACKING_FIBER_AVAILABILITY.VODAFONE_CONNECTED,
            [NewAvailabilityIndicator.GRUEN_GRAU]: TRACKING_FIBER_AVAILABILITY.VODAFONE_CONNECTED,
        },
        [AccessSupplierType.OXG]: {
            [AvailabilityIndicator.PRE_SALES]: TRACKING_FIBER_AVAILABILITY.OXG_PRESALES,
            [AvailabilityIndicator.HOME_PASSED]: TRACKING_FIBER_AVAILABILITY.OXG_HOMEPASSED,
            [NewAvailabilityIndicator.ORANGE_ROT]: TRACKING_FIBER_AVAILABILITY.OXG_HOMEPASSED,
            [FiberConnectedIndicator.CONNECTED]: TRACKING_FIBER_AVAILABILITY.OXG_CONNECTED,
            [NewAvailabilityIndicator.GRUEN_GRAU]: TRACKING_FIBER_AVAILABILITY.OXG_CONNECTED,
        },
        [AccessSupplierType.DTAG]: {
            [AvailabilityIndicator.PRE_SALES]: TRACKING_FIBER_AVAILABILITY.TELEKOM_PRESALES,
            [AvailabilityIndicator.HOME_PASSED]: TRACKING_FIBER_AVAILABILITY.TELEKOM_HOMEPASSED,
            [NewAvailabilityIndicator.ORANGE_ROT]: TRACKING_FIBER_AVAILABILITY.TELEKOM_HOMEPASSED,
            [FiberConnectedIndicator.CONNECTED]: TRACKING_FIBER_AVAILABILITY.TELEKOM_CONNECTED,
            [NewAvailabilityIndicator.GRUEN_GRAU]: TRACKING_FIBER_AVAILABILITY.TELEKOM_CONNECTED,
        },
        [AccessSupplierType.DGW]: {
            [AvailabilityIndicator.PRE_SALES]: TRACKING_FIBER_AVAILABILITY.DEUTSCHE_GLASFASER_PRESALES,
            [AvailabilityIndicator.HOME_PASSED]: TRACKING_FIBER_AVAILABILITY.DEUTSCHE_GLASFASER_HOMEPASSED,
            [NewAvailabilityIndicator.ORANGE_ROT]: TRACKING_FIBER_AVAILABILITY.DEUTSCHE_GLASFASER_HOMEPASSED,
            [FiberConnectedIndicator.CONNECTED]: TRACKING_FIBER_AVAILABILITY.DEUTSCHE_GLASFASER_CONNECTED,
            [NewAvailabilityIndicator.GRUEN_GRAU]: TRACKING_FIBER_AVAILABILITY.DEUTSCHE_GLASFASER_CONNECTED,
        },
    };

    const addAvailability = (suggestion?: JarvisSuggestion) => {
        if (suggestion?.type === 'HomesPassed') {
            result.push(TRACKING_FIBER_AVAILABILITY.VODAFONE_HOMEPASSED);
        } else if (suggestion?.product?.accessSupplier && suggestion?.product?.availabilityIndicator) {
            const val = availabilityMap[suggestion.product.accessSupplier][suggestion.product.availabilityIndicator];
            val && result.push(val);
        }
    };

    addAvailability(primary);
    addAvailability(secondary);

    return result;
};
